import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import Swal from "sweetalert2";
import { Link, useParams } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faPencilAlt,
  faTrash,
  faTimes,
  faArrowUp,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import LoadingSpinner from "../../components/LoadingSpinner";
import { set } from "date-fns";
import { PermissionWrapper, hasPermission } from "../../helpers/permission";


const ViewMessage = () => {
  const { id } = useParams();
  const [portfolio, setPortfolio] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewMessage, setViewMessage] = useState(null);
  const [editForm, setEditForm] = useState({
    id: "",
    service_id: "",
    heading: "",
    message: "",
    files: null,
    old_file_url: "",
  });

  // Fetch portfolio details and messages
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        // Fetch portfolio details and messages
        const response = await axios.get(`${API_URL}portfolio/${id}/message`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data && response.data.success) {
          setPortfolio(response.data.data.service);
          setMessages(response.data.data.messages || []);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        showToast.error("Failed to load portfolio details");
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const formDataToSend = new FormData();

      formDataToSend.append("id", editForm.id);
      formDataToSend.append("service_id", editForm.service_id);
      formDataToSend.append("heading", editForm.heading);
      formDataToSend.append("message", editForm.message);
      formDataToSend.append("old_file_url", editForm.old_file_url);
      if (editForm.files) {
        formDataToSend.append("files", editForm.files);
      }

      await axios.post(
        `${API_URL}portfolio/message/save`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      showToast.success("Message updated successfully!");
      setShowEditModal(false);

      // Refresh messages
      const response = await axios.get(`${API_URL}portfolio/${id}/message`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.success) {
        setMessages(response.data.data.messages || []);
      }
    } catch (err) {
      console.error("Error updating message:", err);
      showToast.error(
        err.response?.data?.message || "Failed to update message"
      );
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value, files } = e.target;
    setEditForm({
      ...editForm,
      [name]: files ? files[0] : value,
    });
  };

  const handleEditorChange = (content) => {
    setEditForm({
      ...editForm,
      message: content,
    });
  };

  const openEditModal = (message) => {
    setEditForm({
      id: message.id,
      service_id: message.service_id,
      heading: message.heading,
      message: message.message,
      file: null,
      old_file_url: message.file || "",
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditForm({
      id: "",
      service_id: "",
      heading: "",
      message: "",
      file: null,
      old_file_url: "",
    });
  };

  const openViewModal = (message) => {
    setViewMessage(message);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewMessage(null);
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You will not be able to recover this message!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, cancel!",
      });

      if (result.isConfirmed) {
        const token = localStorage.getItem("authToken");
        await axios.delete(
          `${API_URL}portfolio/message/${messageId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        showToast.success("Message deleted successfully!");

        // Refresh messages
        const response = await axios.get(`${API_URL}portfolio/${id}/message`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data && response.data.success) {
          setMessages(response.data.data.messages || []);
        }
      }
    } catch (err) {
      console.error("Error deleting message:", err);
      showToast.error(
        err.response?.data?.message || "Failed to delete message"
      );
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!portfolio) {
    return <div className="text-center p-5">Portfolio not found</div>;
  }

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        <div className="row pb-2">
          <div className="col-md-6">
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/Dashboard">
                  <FontAwesomeIcon icon={faHome} /> Home
                </a>
              </li>
              <li className="breadcrumb-item active">Manage Portfolio</li>
            </ul>
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading">
            <span className="panel-title">Portfolio Details</span>
          </div>

          <div className="panel-body">
            <div className="row">
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Portfolio Type: </span>
                <span className="ms-2">{portfolio.segment_name}</span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Portfolio Name: </span>
                <span className="ms-2">{portfolio.service_name}</span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Investment Horizon: </span>
                <span className="ms-2">
                  {portfolio.investment_period || "N/A"}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Rebalance Frequency: </span>
                <span className="ms-2">{portfolio.rebalance_frequency}</span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Asset Class: </span>
                <span className="ms-2">{portfolio.asset_name || "N/A"}</span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Volatility: </span>
                <span className="ms-2">{portfolio.volatility}</span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Last Rebalance Date: </span>
                <span className="ms-2">
                  {new Date(portfolio.last_rebalance_date).toLocaleDateString()}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Next Rebalance Date: </span>
                <span className="ms-2">
                  {new Date(portfolio.next_rebalance_date).toLocaleDateString()}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Min Investment: </span>
                <span className="ms-2">
                  {formatCurrency(portfolio.min_investment)}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">CAGR: </span>
                <span className="ms-2">{portfolio.cagr}</span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Launch Date: </span>
                <span className="ms-2">
                  {new Date(portfolio.launch_date).toLocaleDateString()}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Constituents: </span>
                <span className="ms-2">{portfolio.constituents || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="table-responsive panel panel-default with-rounded-corner with-shadow p-20 tbl_head_sticky">
          <table className="table">
            <thead>
              <tr>
                <th style={{ textAlign: "center" }}>#</th>
                <th>Heading</th>
                <th>File</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {messages.length > 0 ? (
                messages.map((message, index) => (
                  <tr key={message.id}>
                    <td
                      style={{ textAlign: "center", verticalAlign: "middle" }}
                    >
                      {index + 1}
                    </td>
                    <td>{message.heading}</td>
                    <td>
                      {message.file && (
                        <div>
                          <a
                            href={message.file}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              alt="preview not available"
                              src={message.file}
                              width="150px"
                              style={{ maxHeight: "150px", objectFit: "cover" }}
                            />
                          </a>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="d-flex">
                        <button
                          onClick={() => openViewModal(message)}
                          className="btn btn-info btn-xs me-1"
                          title="View Message"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <PermissionWrapper module="portfoliomessage" action="edit">
                          <button
                            onClick={() => openEditModal(message)}
                            className="btn btn-warning btn-xs me-1"
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                        </PermissionWrapper>
                        <PermissionWrapper module="portfoliomessage" action="delete">
                          <button
                            onClick={() => handleDeleteMessage(message.id)}
                            className="btn btn-danger btn-xs"
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </PermissionWrapper>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center p-4">
                    No Messages Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Message Modal */}
      {showEditModal && (
        <>
          <div className="modal-backdrop fade show"></div>

          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-xl" role="document">
              <div className="modal-content">
                {/* Modal Header */}
                <div className="modal-header d-flex align-items-center">
                  <h5 className="modal-title">Edit Message</h5>
                  <button
                    type="button"
                    className="btn-close ms-auto"
                    aria-label="Close"
                    onClick={closeEditModal}
                  ></button>
                </div>

                {/* Modal Form */}
                <form onSubmit={handleEditSubmit} encType="multipart/form-data">
                  <div className="modal-body">
                    <input type="hidden" name="id" value={editForm.id} />
                    <input
                      type="hidden"
                      name="service_id"
                      value={editForm.service_id}
                    />
                    <input
                      type="hidden"
                      name="old_file_url"
                      value={editForm.old_file_url}
                    />

                    <div className="row">
                      {/* Heading */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label className="control-label">Heading</label>
                          <input
                            type="text"
                            name="heading"
                            className="form-control"
                            placeholder="Enter Heading"
                            value={editForm.heading}
                            onChange={handleEditInputChange}
                            required
                          />
                        </div>
                      </div>

                      {/* Message with ReactQuill */}
                      <div className="col-md-12">
                        <div className="form-group">
                          <label className="control-label">Message</label>
                          <ReactQuill
                            theme="snow"
                            value={editForm.message}
                            onChange={(content) =>
                              setEditForm({ ...editForm, message: content })
                            }
                            modules={{
                              toolbar: [
                                ["bold", "italic", "underline"],
                                [{ list: "ordered" }, { list: "bullet" }],
                                ["link", "image"],
                                ["clean"],
                              ],
                            }}
                          />
                        </div>
                      </div>

                      {/* Upload Image */}
                      <div className="col-md-12">
                        <div className="form-group">
                          <label className="control-label">Upload Image</label>
                          <input
                            type="file"
                            name="files"
                            className="form-control"
                            onChange={handleEditInputChange}
                            accept="image/*"
                          />
                          {editForm.old_file_url && (
                            <div className="mt-2">
                              <small className="text-muted">
                                Current file:{" "}
                                <a
                                  href={editForm.old_file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View
                                </a>
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeEditModal}
                    >
                      Close
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Save Message
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* View Message Modal */}
      {showViewModal && viewMessage && (
        <>
          <div className="modal-backdrop fade show"></div>

          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                {/* Modal Header */}
                <div className="modal-header d-flex align-items-center">
                  <h5 className="modal-title">View Message</h5>
                  <button
                    type="button"
                    className="btn-close ms-auto"
                    aria-label="Close"
                    onClick={closeViewModal}
                  ></button>
                </div>

                {/* Modal Body */}
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <h6 className="fw-bold">Heading:</h6>
                      <p>{viewMessage.heading}</p>
                    </div>

                    <div className="col-md-12 mb-3">
                      <h6 className="fw-bold">Message:</h6>
                      <div
                        className="border p-3 rounded"
                        dangerouslySetInnerHTML={{
                          __html: viewMessage.message,
                        }}
                      />
                    </div>

                    {viewMessage.file && (
                      <div className="col-md-12 mb-3">
                        <h6 className="fw-bold">Attached File:</h6>
                        <div className="text-center">
                          <a
                            href={viewMessage.file}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              alt="preview not available"
                              src={viewMessage.file}
                              className="img-fluid"
                              style={{ maxHeight: "300px", objectFit: "contain" }}
                            />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeViewModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <FontAwesomeIcon icon={faArrowUp} />
      </a>
    </div>
  );
};

export default ViewMessage;
